/**
 * Permission constants aligned with the backend Permissions enum.
 * The frontend never resolves role→permissions — the API returns the user's
 * effective permissions on house.user_role.permissions.
 */
export const Permission = {
	// Assistant
	ASSISTANT_QUERY: 'ASSISTANT_QUERY',
	ASSISTANT_MANAGE: 'ASSISTANT_MANAGE',

	// House management
	HOUSE_READ: 'HOUSE_READ',
	HOUSE_UPDATE: 'HOUSE_UPDATE',
	HOUSE_DELETE: 'HOUSE_DELETE',
	HOUSE_ACCESS_MANAGEMENT_MANAGE: 'HOUSE_ACCESS_MANAGEMENT_MANAGE',
	HOUSE_ADMIN: 'HOUSE_ADMIN',

	// Documents
	HOUSE_DOCUMENT_UPLOAD: 'HOUSE_DOCUMENT_UPLOAD',
	HOUSE_DOCUMENT_LIST: 'HOUSE_DOCUMENT_LIST',
	HOUSE_DOCUMENT_DELETE: 'HOUSE_DOCUMENT_DELETE',
	HOUSE_DOCUMENT_DOWNLOAD: 'HOUSE_DOCUMENT_DOWNLOAD',
	HOUSE_DOCUMENT_PRIVATE_ALL: 'HOUSE_DOCUMENT_PRIVATE_ALL',

	/**
	 * Propose a document share — push a document from this house to another (the owner's property).
	 * Owner only for now (not part of the document aggregates).
	 */

	HOUSE_DOCUMENT_SHARE_PROPOSED: 'HOUSE_DOCUMENT_SHARE_PROPOSED',

	/**
	 * Accept/reject documents pushed to the house (pending shares). Owner/Admin only — not part of
	 * # the document aggregates, so Contributors (public-doc access) do not get it
	 */

	HOUSE_DOCUMENT_SHARE_RESOLVE: 'HOUSE_DOCUMENT_SHARE_RESOLVE',

	/**
	 * Schedule — one permission per verb, not one for every write.
	 *
	 * A contributor records work on the plan without deciding what is on it: they hold
	 * complete, note and attach, and nothing else here. The backend's aggregates
	 * (`_HOUSE_SCHEDULE_WORK` and friends) expand before the API answers, so what arrives on
	 * `house.user_role.permissions` is these leaves.
	 */
	HOUSE_SCHEDULE_VIEW: 'HOUSE_SCHEDULE_VIEW',
	HOUSE_SCHEDULE_COMPLETE: 'HOUSE_SCHEDULE_COMPLETE',
	HOUSE_SCHEDULE_NOTE: 'HOUSE_SCHEDULE_NOTE',
	HOUSE_SCHEDULE_ATTACH: 'HOUSE_SCHEDULE_ATTACH',
	HOUSE_SCHEDULE_SNOOZE: 'HOUSE_SCHEDULE_SNOOZE',
	HOUSE_SCHEDULE_SKIP: 'HOUSE_SCHEDULE_SKIP',
	HOUSE_SCHEDULE_RESCHEDULE: 'HOUSE_SCHEDULE_RESCHEDULE',
	HOUSE_SCHEDULE_PLACEMENT: 'HOUSE_SCHEDULE_PLACEMENT',
	HOUSE_SCHEDULE_CREATE: 'HOUSE_SCHEDULE_CREATE',
	HOUSE_SCHEDULE_KEEP: 'HOUSE_SCHEDULE_KEEP',
	HOUSE_SCHEDULE_DISMISS: 'HOUSE_SCHEDULE_DISMISS',
	HOUSE_SCHEDULE_EDIT: 'HOUSE_SCHEDULE_EDIT',
	HOUSE_SCHEDULE_DELETE: 'HOUSE_SCHEDULE_DELETE',

	// Network & Alerts
	HOUSE_NETWORK_MANAGE: 'HOUSE_NETWORK_MANAGE',
	HOUSE_NETWORK_VIEW: 'HOUSE_NETWORK_VIEW',
	ALERTS_MANAGE: 'ALERTS_MANAGE',
} as const;

export type Permission = (typeof Permission)[keyof typeof Permission];

/**
 * Every permission that changes the plan, as against reading it.
 *
 * Held here rather than rebuilt per caller: "may this person change the schedule at all" is a
 * question several surfaces ask, and a list assembled at each of them drifts the moment a verb is
 * added. Which *particular* verb a control needs is a finer question — see `planActionPermissions`.
 */
export const SCHEDULE_WRITE_PERMISSIONS: readonly Permission[] = [
	Permission.HOUSE_SCHEDULE_COMPLETE,
	Permission.HOUSE_SCHEDULE_NOTE,
	Permission.HOUSE_SCHEDULE_ATTACH,
	Permission.HOUSE_SCHEDULE_SNOOZE,
	Permission.HOUSE_SCHEDULE_SKIP,
	Permission.HOUSE_SCHEDULE_RESCHEDULE,
	Permission.HOUSE_SCHEDULE_PLACEMENT,
	Permission.HOUSE_SCHEDULE_CREATE,
	Permission.HOUSE_SCHEDULE_KEEP,
	Permission.HOUSE_SCHEDULE_DISMISS,
	Permission.HOUSE_SCHEDULE_EDIT,
	Permission.HOUSE_SCHEDULE_DELETE,
];

/**
 * Checks whether a permissions array includes a specific permission.
 * Works against the server-provided permissions from house.user_role.
 */
export const hasPermission = (permissions: readonly string[] | undefined | null, permission: Permission): boolean => {
	if (!permissions) return false;
	return permissions.includes(permission);
};

/**
 * Checks whether a permissions array includes at least one of the given permissions.
 */
export const hasAnyPermission = (
	permissions: readonly string[] | undefined | null,
	required: readonly Permission[],
): boolean => {
	if (!permissions || required.length === 0) return false;
	return required.some((p) => permissions.includes(p));
};
